"use client";

import { useMutation } from "convex/react";
import type {
  FunctionReference,
  FunctionArgs,
  FunctionReturnType,
} from "convex/server";
import {
  useAsyncCallWithState,
  type AsyncCallState,
} from "./use-async-call-with-state";

interface UseMutationWithStateOptions<TReturn> {
  onSuccess?: (result: TReturn) => void;
  onError?: (errorMessage: string) => void;
}

interface UseMutationWithStateReturn<TArgs, TReturn> extends AsyncCallState {
  mutate: (args: TArgs) => Promise<TReturn | undefined>;
}

export function useMutationWithState<
  TMutation extends FunctionReference<"mutation">,
>(
  mutation: TMutation,
  options?: UseMutationWithStateOptions<FunctionReturnType<TMutation>>,
): UseMutationWithStateReturn<
  FunctionArgs<TMutation>,
  FunctionReturnType<TMutation>
> {
  const rawMutate = useMutation(mutation);
  const { execute, ...state } = useAsyncCallWithState(rawMutate, {
    ...options,
    retryable: true,
  });

  return { mutate: execute, ...state };
}
